import * as React from 'react';
import { Group, createStyles, Textarea as TextareaBase, TextareaProps } from '@mantine/core';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        label: {
            ...commonFieldStyles.label,
            top: '1px',
            left: '1px',
            right: '21px',
            borderRadius: '4px',
            background: '#fff'
        }
    };
});

export interface CustomTextareaProps extends TextareaProps, GenericFieldProps {
    key?: string;
    type?: string;
}

export const Textarea: React.FC<CustomTextareaProps> = ({
    readOnly,
    label,
    labelKey,
    descriptionKey,
    tooltipKey,
    parentform,
    targetperiod,
    highlight,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const textareaLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });

    return (
        <TextareaBase
            classNames={{
                root: classes.root,
                input: cx(classes.input, {
                    [classes.readonlyInput]: readOnly,
                    [classes.inputHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                wrapper: cx(classes.wrapper, {
                    [classes.readonlyWrapper]: readOnly
                }),
                error: classes.error,
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                })
            }}
            label={
                <>
                    <span>{textareaLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            {...props}
        />
    );
};
